import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { 
  LayoutDashboard, CalendarDays, Users, 
  Settings, UserCircle, ClipboardList, BarChart3,
  Dumbbell, Building2, Receipt, Ticket, LogOut, User, MessageSquare, CreditCard, ShoppingCart, HelpCircle, Activity, Star, Compass
} from 'lucide-react';
import { Button } from './ui/button';

const clientNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/dashboard' },
  { icon: Compass, label: 'Browse Studios', href: '/studios' },
  { icon: CalendarDays, label: 'Schedule', href: '/schedule' },
  { icon: ClipboardList, label: 'My Bookings', href: '/bookings' },
  { icon: Ticket, label: 'ClassPasses', href: '/memberships' },
  { icon: Star, label: 'Favourites', href: '/favourites' },
  { icon: CreditCard, label: 'Billing', href: '/billing' },
  { icon: UserCircle, label: 'Profile', href: '/profile' },
];

const studioOwnerNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/studio/dashboard' },
  { icon: CalendarDays, label: 'Classes', href: '/studio/classes' },
  { icon: Ticket, label: 'ClassPasses', href: '/studio/classpasses' },
  { icon: Users, label: 'Instructors', href: '/studio/instructors' },
  { icon: UserCircle, label: 'Clients', href: '/studio/clients' },
  { icon: ClipboardList, label: 'Bookings', href: '/studio/bookings' },
  { icon: ShoppingCart, label: 'POS', href: '/studio/pos' },
  { icon: Receipt, label: 'Vouchers', href: '/studio/vouchers' },
  { icon: BarChart3, label: 'Reports', href: '/studio/reports' },
  { icon: MessageSquare, label: 'Comms Log', href: '/studio/communications' },
  { icon: CreditCard, label: 'Subscription', href: '/studio/subscription' },
  { icon: Settings, label: 'Settings', href: '/studio/settings' },
  { icon: HelpCircle, label: 'Help Centre', href: '/studio/help' },
];

const instructorNavItems = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/instructor/dashboard' },
  { icon: CalendarDays, label: 'My Schedule', href: '/instructor/dashboard' },
];

const superAdminNavItems = [
  { icon: LayoutDashboard, label: 'Platform Overview', href: '/admin/dashboard' },
  { icon: Building2, label: 'Studios', href: '/admin/studios' },
  { icon: Ticket, label: 'Subscriptions', href: '/admin/subscriptions' },
  { icon: Receipt, label: 'Billing', href: '/admin/billing' },
  // Iter-46o: "SMS Packages" entry hidden — SMS is now included in all plans
  // (ClickSend platform account). The page + endpoints stay alive in case
  // we revive paid SMS in future, but the nav doesn't expose them.
  { icon: Activity, label: 'Payment Health', href: '/admin/payment-health' },
  { icon: Settings, label: 'Settings', href: '/admin/settings' },
];

/**
 * SidebarContent — Iter-53
 *
 * The nav body itself, without the outer <aside> chrome. Rendered both
 * inside the fixed desktop rail (`Sidebar`) and inside the mobile Sheet
 * drawer (`DashboardLayout`), so links, active state and logout stay in
 * one place.
 *
 * `onNavigate` is called after any link click so the drawer can close
 * itself on mobile — a no-op on desktop.
 */
export const SidebarContent = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
    if (onNavigate) onNavigate();
  };

  const getNavItems = () => {
    if (user?.role === 'super_admin') return superAdminNavItems;
    if (user?.role === 'studio_owner') return studioOwnerNavItems;
    if (user?.role === 'instructor') return instructorNavItems;
    return clientNavItems;
  };

  const navItems = getNavItems();
  const profileUrl = '/profile';

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b">
        <Link to="/" className="flex items-center gap-2" onClick={onNavigate}>
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <Dumbbell className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-bold text-lg tracking-tight">StudioScheduler</span>
        </Link>
      </div>

      {/* User Profile Section */}
      <Link
        to={profileUrl}
        onClick={onNavigate}
        className="p-4 border-b hover:bg-muted transition-colors"
        data-testid="sidebar-user-profile"
      >
        <div className="flex items-center gap-3">
          {user?.photo_url ? (
            <img
              src={user.photo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : user.photo_url}
              alt={user.first_name || 'User'}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-muted-foreground truncate capitalize">
              {user?.role?.replace('_', ' ')}
            </p>
          </div>
        </div>
      </Link>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onNavigate}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/ /g, '-')}`}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                isActive
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t space-y-4">
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-destructive"
          onClick={handleLogout}
          data-testid="sidebar-logout"
        >
          <LogOut className="w-5 h-5 mr-3" />
          Logout
        </Button>
        <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-orange-500/10 border border-primary/20">
          <h4 className="font-semibold text-sm mb-1">Need Help?</h4>
          <p className="text-xs text-muted-foreground mb-3">
            Contact our support team for assistance.
          </p>
          {/* Bug R2 fix (Iter-66): the button previously had no onClick
              handler and did nothing. Studio owners now land on the Help
              Centre; everyone else gets a mailto so they can reach out
              directly. */}
          <button
            className="text-xs font-medium text-primary hover:underline"
            onClick={() => {
              if (onNavigate) onNavigate();
              if (user?.role === 'studio_owner' || user?.role === 'super_admin') {
                navigate('/studio/help');
              } else {
                window.location.href = 'mailto:support@studioscheduler.ie?subject=Support%20request';
              }
            }}
            data-testid="sidebar-get-support-btn"
          >
            Get Support
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Sidebar — desktop-only rail (>= lg breakpoint / 1024px).
 * On smaller screens the mobile drawer in DashboardLayout shows `SidebarContent`
 * inside a Sheet.
 */
export const Sidebar = () => (
  <aside
    className="w-64 border-r bg-card/50 backdrop-blur-xl h-screen sticky top-0 hidden lg:block"
    data-testid="sidebar-desktop"
  >
    <SidebarContent />
  </aside>
);
